import React, { useState, useEffect, useRef, useMemo } from 'react';
import { MdPersonAdd, MdExitToApp, MdSwapHoriz } from 'react-icons/md';
import styles from '../Styles/ChatOverview.module.css';
import formatTimestamp from '../Functions/formatTimestamp';
import { useAuth } from '../Components/AuthContext';

const ChatOverview = () => {
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [messageInput, setMessageInput] = useState('');
    const [employees, setEmployees] = useState([]);
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [viewMode, setViewMode] = useState('my');
    const [dividerIndex, setDividerIndex] = useState(null);
    const [healthcareWorkers, setHealthcareWorkers] = useState([]);
    const { token, user } = useAuth();
    const currentUserId = user?.user_id;
    const bottomRef = useRef(null);
    const dropdownRef = useRef(null);
    const addButtonRef = useRef(null);
    const ws = useRef(null);
    const listWs = useRef(null);
    const url = process.env.REACT_APP_API_URL;
    const ws_url = process.env.REACT_APP_WS_URL;

    useEffect(() => {
        fetchConversations();
        fetchEmployees();
    }, [token]);

    useEffect(() => {
        if (!token) return;

        listWs.current = new WebSocket(`${ws_url}/chat/notify?token=${token}`);

        listWs.current.onmessage = (event) => {
            const msg = event.data;
            // you can do a more robust check here if needed
            if (msg.includes("New messages, please call '/chat/list'")) {
                fetchConversations();
            }
        };

        listWs.current.onerror = (err) => {
            console.error('Chat-list WebSocket error', err);
        };

        listWs.current.onclose = () => {
            console.log('Chat-list WebSocket closed');
        };

        return () => {
            if (listWs.current) listWs.current.close();
        };
    }, [token, ws_url]);

    useEffect(() => {
        if (bottomRef.current) {
            bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target) &&
                addButtonRef.current &&
                !addButtonRef.current.contains(event.target)
            ) {
                setShowEmployeeDropdown(false);
            }
        };

        if (showEmployeeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showEmployeeDropdown]);

    useEffect(() => {
        if (!token) return;
        fetch(`${url}/healthcare-worker/list`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then(setHealthcareWorkers)
            .catch(console.error);
    }, [token]);

    const fetchEmployees = async () => {
        try {
            const response = await fetch(`${url}/gmf-employee/list?verified=true`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setEmployees(data);
            } else {
                console.error(
                    'Failed to fetch Global Medic Force accounts list'
                );
            }
        } catch (error) {
            console.error('Error fetching Global Medic Force accounts:', error);
        }
    };

    const getCurrentUserName = () => {
        if (user) {
            return `${user.first_name} ${user.last_name}`;
        }
        return 'Unknown user';
    };

    const fetchConversations = async () => {
        try {
            const response = await fetch(`${url}/chat/list`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setConversations(data);
            } else {
                // handle response error
                console.error('Failed trying to fetch conversations');
            }
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    };

    const setupWebSocket = (chatId) => {
        // Close existing WebSocket connection if one's open
        if (ws.current) {
            ws.current.close();
        }

        // Open new WebSocket connection for selected conversation
        ws.current = new WebSocket(
            `${ws_url}/chat/ws/${chatId}?token=${token}`
        );
        ws.current.onmessage = (event) => {
            const data = event.data;
            const messageObject = JSON.parse(data);

            const newMessage = {
                chat_id: chatId,
                chat_message_type:
                    messageObject.chat_message_type || 'gmf_chat',
                timestamp: messageObject.timestamp,
                message: messageObject.message,
                sender_user_id: messageObject.sender_user_id,
                sender_name: messageObject.sender_name,
                read_ids: messageObject.read_ids,
                hcw_read: messageObject.hcw_read || true,
            };

            setMessages((prevMessages) => [...prevMessages, newMessage]);
        };
        ws.current.onerror = (error) => {
            console.error('WebSocket error', error);
        };
        ws.current.onclose = () => {
            console.log('WebSocket connection closed');
        };
    };

    const selectConversation = (conversation) => {
        // ——— 1) Clear out any old state ———
        if (selectedConversation) {
            // wipe the badge on the old chat
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === selectedConversation.id
                        ? { ...c, unread_messages: 0 }
                        : c
                )
            );
        }
        // clear old messages & divider
        setMessages([]);
        setDividerIndex(null);

        // ——— 2) If they’re not in the chat yet, ask to join ———
        const isGMFInChat = conversation.gmf_ids.includes(currentUserId);
        if (!isGMFInChat) {
            const join = window.confirm(
                'You are not part of this conversation. Would you like to join?'
            );
            if (!join) return;

            // optimistically add them
            joinConversation(conversation.id);
            // treat it just like selecting a normal chat (with 0 unread)
            setSelectedConversation(conversation);
            setupWebSocket(conversation.id);
            fetchMessages(conversation.id, 0);
            return;
        }

        // ——— 3) Normal “already a member” path ———
        setSelectedConversation(conversation);
        setupWebSocket(conversation.id);
        // pass in the unread count so the divider pins correctly
        fetchMessages(conversation.id, conversation.unread_messages);
    };

    const joinConversation = async (chatId) => {
        try {
            const response = await fetch(`${url}/chat/add-gmf/${chatId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                // Successfully joined the chat room
                // Update the conversation list to reflect the change
                fetchConversations();
            } else {
                // Handle error response
                console.error('Failed to join conversation');
            }
        } catch (error) {
            console.error('Error joining conversation:', error);
        }
    };

    const fetchMessages = async (chatId, initialUnreadCount = 0) => {
        try {
            const response = await fetch(`${url}/chat/messages/${chatId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Error fetching messages');
            const data = await response.json();

            // show all messages...
            setMessages(data);

            // …then pin the divider at exactly the right spot:
            if (initialUnreadCount > 0) {
                setDividerIndex(data.length - initialUnreadCount);
            } else {
                setDividerIndex(null);
            }

            // clear the badge in your conversation list immediately:
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === chatId ? { ...c, unread_messages: 0 } : c
                )
            );
        } catch (err) {
            console.error(err);
        }
    };

    const handleSendMessage = () => {
        // if user input is empty, exit
        if (!messageInput.trim()) return;

        // Send message via WS
        ws.current.send(messageInput);

        const messageData = {
            chat_id: selectedConversation.id,
            chat_message_type: 'gmf_chat',
            timestamp: new Date().toISOString(),
            message: messageInput,
            sender_user_id: currentUserId,
            sender_name: getCurrentUserName(),
        };

        setMessages((prevMessages) => [...prevMessages, messageData]);

        // no more divider, no more badge:
        setDividerIndex(null);
        setConversations((prev) =>
            prev.map((c) =>
                c.id === selectedConversation.id
                    ? { ...c, unread_messages: 0 }
                    : c
            )
        );

        setMessageInput('');
    };

    // toggles between 'my' and 'unanswered'
    const toggleViewMode = () => {
        setViewMode((prevMode) => (prevMode === 'my' ? 'unanswered' : 'my'));

        // Clear right conversation panel
        setSelectedConversation(null);
        setMessages([]);
        setDividerIndex(null);
    };

    // function to add GMF employee to a chat by id
    const addEmployeeToChat = async (chatId, employeeId) => {
        try {
            const response = await fetch(
                `${url}/chat/add-gmf/${chatId}?other_gmf_user_id=${employeeId}`,
                {
                    method: 'PATCH',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            if (response.ok) {
                fetchConversations();
                alert('Global Medic Force account added successfully.');
            } else {
                console.error(
                    'Failed to add Global Medic Force account to the conversation'
                );
                alert(
                    'Error adding Global Medic Force account to the conversation'
                );
            }
        } catch (error) {
            console.error('Error adding Global Medic Force account:', error);
        }
    };

    const removeSelfFromChat = async (chatId) => {
        try {
            const response = await fetch(`${url}/chat/remove-gmf/${chatId}`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (response.ok) {
                alert('You have left the chat.');
                setSelectedConversation(null); // Deselect the chat
                setMessages([]); // Clear messages
                fetchConversations(); // Refresh the chat list
            } else {
                console.error('Failed to leave the chat');
                alert('Error leaving the chat');
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    // logic to filter conversations/chats based on viewMode
    const filteredConversations = conversations.filter((conv) => {
        if (viewMode === 'my') {
            return conv.gmf_ids.includes(currentUserId);
        } else {
            return conv.gmf_ids.length === 0;
        }
    });

    // compute the one HCW who belongs to the selected conversation
    const selectedHCW = useMemo(() => {
        if (!selectedConversation) return null;
        return healthcareWorkers.find(
            (hcw) => hcw.user_id === selectedConversation.hcw_id
        );
    }, [selectedConversation, healthcareWorkers]);

    return (
        <div className={styles.container}>
            <div className={styles.sidebar}>
                <div className={styles.conversationHeader}>
                    <h2>{viewMode === 'my' ? 'My Chats' : 'New Chats'}</h2>
                    <div className={styles.headerButtonGroup}>
                        <button
                            onClick={toggleViewMode}
                            className={styles.headerButton}
                            aria-label="Toggle Conversations"
                            title="Switch between your own chats and unanswered chats"
                        >
                            <MdSwapHoriz size={18} />
                        </button>
                    </div>
                </div>

                <ul className={styles.conversationList}>
                    {filteredConversations.map((conv) => (
                        <li
                            key={conv.id}
                            className={`${styles.conversationItem} ${
                                selectedConversation?.id === conv.id
                                    ? styles.selected
                                    : ''
                            }`}
                            onClick={() => selectConversation(conv)}
                        >
                            <span>
                                {conv.name || `Conversation #${conv.id}`}
                                {conv.unread_messages > 0 && (
                                    <span className={styles.unreadBadge}>
                                        {conv.unread_messages}
                                    </span>
                                )}
                            </span>

                            {conv.gmf_ids.length === 0 && (
                                <button
                                    className={styles.joinButton}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        joinConversation(conv.id);
                                    }}
                                >
                                    Join
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            </div>

            <div className={styles.chatWindow}>
                {selectedConversation ? (
                    <>
                        <div className={styles.chatHeader}>
                            <h2>
                                {selectedConversation.name ||
                                    `Conversation ${selectedConversation.id}`}
                            </h2>
                            <div className={styles.headerButtonGroup}>
                                <button
                                    onClick={() => {
                                        const confirmLeave = window.confirm(
                                            'Are you sure you want to leave this chat?'
                                        );
                                        if (confirmLeave)
                                            removeSelfFromChat(
                                                selectedConversation.id
                                            );
                                    }}
                                    className={styles.headerButton}
                                >
                                    <MdExitToApp size={18} /> Leave Chat
                                </button>
                            </div>
                        </div>

                        <div className={styles.chatMessages}>
                            {messages.map((msg, index) => {
                                const isMyMessage =
                                    msg.sender_user_id === currentUserId;
                                const isHCWMessage =
                                    !isMyMessage &&
                                    !selectedConversation.gmf_ids.includes(
                                        msg.sender_user_id
                                    );
                                const isOther = !isMyMessage && !isHCWMessage;
                                const showDivider =
                                    dividerIndex !== null &&
                                    index === dividerIndex;

                                // const unreadCount =
                                //     selectedConversation?.unread_messages || 0;

                                return (
                                    <React.Fragment key={index}>
                                        {showDivider && (
                                            <div
                                                className={styles.unseenDivider}
                                            >
                                                Unseen messages
                                            </div>
                                        )}
                                        <div
                                            className={
                                                `${styles.message} ` +
                                                (isMyMessage
                                                    ? styles.myMessage
                                                    : isHCWMessage
                                                    ? styles.hcwMessage
                                                    : styles.otherMessage)
                                            }
                                        >
                                            <div
                                                className={
                                                    styles.messageContent
                                                }
                                            >
                                                {msg.message}
                                            </div>
                                            <small
                                                className={`${
                                                    styles.messageMeta
                                                } ${
                                                    msg.sender_user_id ===
                                                    currentUserId
                                                        ? styles.metaRight
                                                        : styles.metaLeft
                                                }`}
                                            >
                                                {formatTimestamp(msg.timestamp)}{' '}
                                                - {msg.sender_name}
                                            </small>
                                        </div>
                                    </React.Fragment>
                                );
                            })}

                            <div ref={bottomRef} />
                        </div>

                        <div className={styles.chatInput}>
                            <input
                                type="text"
                                value={messageInput}
                                onChange={(e) =>
                                    setMessageInput(e.target.value)
                                }
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                className={styles.input}
                                placeholder="Type a message..."
                            />
                            <button
                                onClick={handleSendMessage}
                                className={styles.sendButton}
                            >
                                Send
                            </button>
                        </div>
                    </>
                ) : (
                    <div className={styles.noConversationSelected}>
                        <p>Select a conversation to start chatting</p>
                    </div>
                )}
            </div>

            <div className={styles.employeeSidebar}>
                {selectedConversation ? (
                    <>
                        <div className={styles.employeeSidebarHeader}>
                            <h2>Participants</h2>

                            {/* icon-only Add Participant button */}
                            <button
                                className={styles.addIconButton}
                                onClick={() =>
                                    setShowEmployeeDropdown((v) => !v)
                                }
                                aria-label="Add participant"
                                ref={addButtonRef}
                            >
                                <MdPersonAdd size={18} />
                            </button>
                        </div>

                        {selectedHCW && (
                            <div className={styles.hcwInfoCard}>
                                <h3>
                                    {selectedHCW.first_name}{' '}
                                    {selectedHCW.last_name}
                                </h3>
                                <p>
                                    <strong>Profession:</strong>{' '}
                                    {selectedHCW.profession}
                                </p>
                                <p>
                                    <strong>Clinic:</strong>{' '}
                                    {selectedHCW.clinic_name}
                                </p>
                                <p>
                                    <strong>Dept.:</strong>{' '}
                                    {selectedHCW.department}
                                </p>
                                <p>
                                    <strong>Country:</strong>{' '}
                                    {selectedHCW.country_name}
                                </p>
                            </div>
                        )}

                        <ul className={styles.participantList}>
                            {/* <li className={styles.participantItem}>
                                {selectedConversation.name ||
                                    `Conversation ${selectedConversation.id}`}{' '}
                                (HCW)
                            </li> */}
                            {employees
                                .filter((emp) =>
                                    selectedConversation.gmf_ids.includes(
                                        emp.user_id
                                    )
                                )
                                .sort((a, b) => {
                                    if (a.user_id === currentUserId) return -1;
                                    if (b.user_id === currentUserId) return 1;

                                    // Alphabetical sort by last name, then first name
                                    const lastNameCompare =
                                        a.last_name.localeCompare(b.last_name);
                                    if (lastNameCompare !== 0)
                                        return lastNameCompare;

                                    return a.first_name.localeCompare(
                                        b.first_name
                                    );
                                })
                                .map((emp) => (
                                    <li
                                        key={emp.id}
                                        className={styles.participantItem}
                                    >
                                        {emp.first_name} {emp.last_name}
                                        {emp.user_id === currentUserId &&
                                            ' (You)'}
                                    </li>
                                ))}
                        </ul>
                    </>
                ) : (
                    <div className={styles.noConversationSelected}>
                        <p>Select a conversation first</p>
                    </div>
                )}
            </div>
            {showEmployeeDropdown && (
                <div
                    className={styles.employeeDropdownOverlay}
                    ref={dropdownRef}
                >
                    <select
                        value={selectedEmployee}
                        onChange={(e) => setSelectedEmployee(e.target.value)}
                    >
                        <option value="">Select a GMF Account</option>
                        {employees
                            .filter(
                                (emp) =>
                                    !selectedConversation?.gmf_ids.includes(
                                        emp.user_id
                                    )
                            )
                            .map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.first_name} {emp.last_name}
                                </option>
                            ))}
                    </select>
                    <button
                        className={styles.confirmButton}
                        onClick={() => {
                            if (!selectedEmployee) {
                                return alert('Please select an employee');
                            }
                            addEmployeeToChat(
                                selectedConversation.id,
                                selectedEmployee
                            );
                            setSelectedEmployee('');
                            setShowEmployeeDropdown(false);
                        }}
                    >
                        Confirm
                    </button>
                </div>
            )}
        </div>
    );
};

export default ChatOverview;
